import React from "react";
import { Box, TableCell, TableRow } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DotLegalMultiSelect, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import QuestionnaireRisk from "@common/components/questionnaireComponents/questionnaireRisk/QuestionnaireRisk";
import QuestionnaireEvaluation from "@common/components/questionnaireComponents/questionnaireEvaluation/QuestionnaireEvaluation";
import { useTranslation } from "@localization/useTranslation";
import InternalAssessmentReportButtons from "../../internalAssessmentReportButtons/InternalAssessmentReportButtons";
import { InternalAssessmentReportRow, InternalAssessmentReportTableProps } from "../InternalAssessmentReportTable.types";
import useInternalAssessmentReportCompletedAssessments from "./InternalAssessmentReportCompletedAssessments.hooks";
import ManagementReportTableSection, {
    managementReportFilterGridSizes,
} from "@common/components/managementReport/managementReportTableSection/ManagementReportTableSection";

function InternalAssessmentReportCompletedAssessments(props: InternalAssessmentReportTableProps) {
    const { translateString } = useTranslation();
    const {
        rows,
        onDownloadReportAsPdf,
        getReportPdfUrl,
        isDownloadingPdf,
        searchedRiskLevel,
        setSearchedRiskLevel,
        riskLevelFilterOptions,
        searchedScore,
        setSearchedScore,
        scoreFilterOptions,
        searchedEvaluation,
        setSearchedEvaluation,
        evaluationFilterOptions,
    } = useInternalAssessmentReportCompletedAssessments(props);

    function getHeaders() {
        const headers = Array<ITableHeader<InternalAssessmentReportRow>>();
        headers.push({ property: "assigneeName", text: translateString("assigned"), align: "left", showOnMobile: true });
        headers.push({ property: "riskLevel", text: translateString("riskLevel"), align: "left", showOnMobile: true });
        headers.push({ property: "scoreString", text: translateString("score"), align: "left", showOnMobile: true });
        headers.push({ property: "evaluationStatus", text: translateString("evaluation"), align: "left", showOnMobile: true });

        if (props.isPdf) {
            headers.push({ property: "evaluation", text: translateString("evaluationText"), align: "left", showOnMobile: true });
        }

        headers.push({ property: "questionnaireId", text: translateString("assessmentReport"), align: "left", showOnMobile: true });

        // Hidden header - allowing search
        headers.push({ property: "statusString", text: "", align: "left", showOnMobile: true, hideHeader: true });

        return headers;
    }

    function getSearchFields() {
        return (
            <React.Fragment>
                <Grid size={managementReportFilterGridSizes}>
                    <DotLegalMultiSelect
                        onChange={setSearchedRiskLevel}
                        chosenOptions={searchedRiskLevel}
                        label={translateString("riskLevel")}
                        placeholder={translateString("riskLevel")}
                        isLoading={props.isLoading}
                        options={riskLevelFilterOptions}
                        noMargin
                        limit={1}
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>
                <Grid size={managementReportFilterGridSizes}>
                    <DotLegalMultiSelect
                        onChange={setSearchedScore}
                        chosenOptions={searchedScore}
                        label={translateString("score")}
                        placeholder={translateString("score")}
                        isLoading={props.isLoading}
                        options={scoreFilterOptions}
                        noMargin
                        limit={1}
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>
                <Grid size={managementReportFilterGridSizes}>
                    <DotLegalMultiSelect
                        onChange={setSearchedEvaluation}
                        chosenOptions={searchedEvaluation}
                        label={translateString("evaluation")}
                        placeholder={translateString("evaluation")}
                        isLoading={props.isLoading}
                        options={evaluationFilterOptions}
                        noMargin
                        limit={1}
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>
            </React.Fragment>
        );
    }

    return (
        <ManagementReportTableSection
            title={translateString("completed")}
            count={props.assessments.length}
            isPdf={props.isPdf}
            headers={getHeaders()}
            defaultOrderBy={"assigneeName"}
            data={rows}
            searchFields={getSearchFields()}
            renderRow={(row) => (
                <TableRow key={row.id}>
                    <TableCell>
                        <Box component={"span"} sx={{ fontWeight: "bold" }}>
                            {row.assigneeName}
                        </Box>
                    </TableCell>
                    <TableCell>
                        <QuestionnaireRisk riskLevel={row.riskLevel} riskName={row.riskName} scoreEnabled={row.scoreEnabled} status={row.status} />
                    </TableCell>
                    <TableCell align="right">{row.scoreString}</TableCell>
                    <TableCell>
                        <QuestionnaireEvaluation evaluationStatus={row.evaluationStatus} evaluation={row.evaluation} />
                    </TableCell>
                    {props.isPdf && <TableCell>{row.evaluation}</TableCell>}
                    <TableCell>
                        <InternalAssessmentReportButtons
                            row={row}
                            isDownloadingPdf={isDownloadingPdf(row.id)}
                            reportPdfUrl={getReportPdfUrl(row, false, row.hasRecommendations)}
                            onDownloadReportPdf={onDownloadReportAsPdf}
                        />
                    </TableCell>
                </TableRow>
            )}
        />
    );
}

export default InternalAssessmentReportCompletedAssessments;
